import {
  IconArrowOutOfBox,
  IconCalendarDays
} from '@sequencehq/core-components'
import {
  ExecutionSummaryComponent,
  ExecutionSummaryComponentProps
} from 'Automations/nodeDefinitions/types'
import { NodeTimeline, useNodeTimelineVariant } from 'Automations/components'
import {
  CancelledItem,
  FailedItem,
  hasAllTasksInProgress,
  hasCancelledTask,
  TerminalStateItem
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import { useMemo } from 'react'
import { BillingSchedulePreviewCard } from 'BillingSchedules/index'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

export const CreateScheduleExecutionSummary: ExecutionSummaryComponent = ({
  tasks,
  automationExecution,
  isLatestNodeExecution
}) => {
  const showPreviews = useNodeTimelineVariant() === 'DEFAULT'
  const {
    tasksInProgress,
    taskCancelled,
    billingScheduleId,
    createScheduleReviewTask
  } = useCreateScheduleData(tasks)

  if (taskCancelled) {
    return (
      <CancelledItem
        label="Schedule creation cancelled"
        timestamp={automationExecution.createTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  if (tasksInProgress) {
    return (
      <TerminalStateItem
        icon={<IconArrowOutOfBox />}
        label="Schedule is being created"
        timestamp={automationExecution.createTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  if (!billingScheduleId || createScheduleReviewTask?.status !== 'COMPLETED') {
    return (
      <FailedItem
        label="Schedule creation failed"
        timestamp={automationExecution.createTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  return (
    <>
      <NodeTimeline.Item
        icon={<IconCalendarDays />}
        showStem={!isLatestNodeExecution}
      >
        <NodeTimeline.Title>
          <NodeTimeline.UserAction
            label="Schedule created"
            timestamp={automationExecution.createTime}
          />
        </NodeTimeline.Title>
        {showPreviews && (
          <BillingSchedulePreviewCard scheduleId={billingScheduleId ?? ''} />
        )}
      </NodeTimeline.Item>
    </>
  )
}

const useCreateScheduleData = (
  tasks: ExecutionSummaryComponentProps['tasks']
) => {
  const tasksInProgress = useMemo(() => hasAllTasksInProgress(tasks), [tasks])

  const taskCancelled = useMemo(() => hasCancelledTask(tasks), [tasks])

  const createScheduleReviewTask = useMemo(() => {
    return tasks.find(
      task =>
        task.inputData.taskExecutorConfiguration?.executor ===
        TASK_EXECUTORS.createSchedule
    )
  }, [tasks])

  const { billingScheduleId } = useMemo(() => {
    return {
      billingScheduleId: createScheduleReviewTask?.outputData
        ?.billingScheduleId as string | undefined
    }
  }, [createScheduleReviewTask])

  return {
    tasksInProgress,
    taskCancelled,
    billingScheduleId,
    createScheduleReviewTask
  }
}
